<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        Skills Library
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`Manage your skills library with video tutorials and demonstrations.`"
          >info</i
        >
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'new-skill-library' }">
          Add to Library
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <!-- Tabs -->
            <b-tabs v-model="activeTab" @input="onTabChange" content-class="mt-3">
              <!-- My Skills Tab -->
              <b-tab title="My Skills" active>
                <div class="row align-items-center mb-3" style="white-space: nowrap">
                  <div class="col-lg-auto">
                    <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                      <b-form-input class="w-lg-auto" placeholder="Search Skills" v-model="searchTerm" @input="onSearch" />
                      <b-btn variant="flush" type="submit">
                        <md-icon>search</md-icon>
                      </b-btn>
                    </form>
                  </div>
                </div>

                <b-table
                  :fields="mySkillsTableColumns"
                  :items="libraryItems"
                  v-if="libraryItems.length"
                  :busy="isLoading"
                  head-variant="light"
                  class="table-nowrap sticky-lcol-table"
                  hover
                  responsive
                  no-local-sorting
                  @sort-changed="onSortChange"
                  @row-clicked="editLibraryItem"
                >
                  <template #table-busy>
                    <div class="text-center my-2">
                      <b-spinner class="align-middle mr-2"></b-spinner>
                      <strong>Loading...</strong>
                    </div>
                  </template>

                  <template #cell(thumbnail)="data">
                    <img
                      :src="data.item.thumbnail_url"
                      class="rounded"
                      style="width: 40px; height: 40px; object-fit: cover"
                      v-if="data.item.thumbnail_url"
                    />
                    <video
                      :src="data.item.video_url"
                      class="rounded"
                      style="width: 40px; height: 40px; object-fit: cover"
                      v-else
                    ></video>
                  </template>

                  <template #cell(description)="data">
                    {{ stripHtml(data.item.description) }}
                  </template>

                  <template #cell(program)="data">
                    <span v-if="data.item.program">{{ getProgramName(data.item.program) }}</span>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template #cell(created_at)="data">
                    {{ formatDate(data.item.created_at) }}
                  </template>

                  <template #cell(actions)="data">
                    <router-link
                      :to="{ name: 'edit-skill-library', params: { id: data.item.id } }"
                      class="svg-icon mr-2"
                      v-b-popover.hover.left
                      :title="$t('generalMsgs.edit')"
                    >
                      <md-icon class="text-info">edit</md-icon>
                    </router-link>
                    <a
                      href="#"
                      @click.prevent="removeLibraryItem(data.item.id)"
                      v-b-popover.hover.left
                      :title="$t('generalMsgs.remove')"
                    >
                      <md-icon class="text-danger">delete</md-icon>
                    </a>
                  </template>
                </b-table>
                <div v-if="!libraryItems.length && !isLoading" class="text-center py-4">
                  <p class="text-muted">No skills found</p>
                </div>
                <div class="card-footer" v-if="libraryItems.length">
                  <pagination
                    v-model="currentPage"
                    :total-rows="totalItems"
                    :per-page="perPage"
                    @change="onPageChange"
                    aria-controls="skills-table"
                  />
                </div>
              </b-tab>

              <!-- Transition Skills Tab -->
              <b-tab title="Transition Skills">
                <div class="row align-items-center mb-3" style="white-space: nowrap">
                  <div class="col-lg-auto">
                    <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                      <b-form-input class="w-lg-auto" placeholder="Search Skills" v-model="searchTerm" @input="onSearch" />
                      <b-btn variant="flush" type="submit">
                        <md-icon>search</md-icon>
                      </b-btn>
                    </form>
                  </div>
                </div>

                <b-table
                  :fields="transitionSkillsTableColumns"
                  :items="libraryItems"
                  v-if="libraryItems.length"
                  :busy="isLoading"
                  head-variant="light"
                  class="table-nowrap sticky-lcol-table"
                  hover
                  responsive
                  no-local-sorting
                  @sort-changed="onSortChange"
                  @row-clicked="viewLibraryItem"
                >
                  <template #table-busy>
                    <div class="text-center my-2">
                      <b-spinner class="align-middle mr-2"></b-spinner>
                      <strong>Loading...</strong>
                    </div>
                  </template>

                  <template #cell(thumbnail)="data">
                    <img
                      :src="data.item.thumbnail_url"
                      class="rounded"
                      style="width: 40px; height: 40px; object-fit: cover"
                      v-if="data.item.thumbnail_url"
                    />
                    <video
                      :src="data.item.video_url"
                      class="rounded"
                      style="width: 40px; height: 40px; object-fit: cover"
                      v-else
                    ></video>
                  </template>

                  <template #cell(description)="data">
                    {{ stripHtml(data.item.description) }}
                  </template>

                  <template #cell(program)="data">
                    <span v-if="data.item.program">{{ getProgramName(data.item.program) }}</span>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template #cell(created_at)="data">
                    {{ formatDate(data.item.created_at) }}
                  </template>

                  <template #cell(actions)="data">
                    <router-link
                      :to="{
                        name: 'view-skill-library',
                        params: { id: data.item.id },
                      }"
                      class="svg-icon mr-2"
                      v-b-popover.hover.left
                      title="View Skill"
                    >
                      <md-icon class="text-info">menu_book</md-icon>
                    </router-link>
                  </template>
                </b-table>
                <div v-if="!libraryItems.length && !isLoading" class="text-center py-4">
                  <p class="text-muted">No transition skills found</p>
                </div>
                <div class="card-footer" v-if="libraryItems.length">
                  <pagination
                    v-model="currentPage"
                    :total-rows="totalItems"
                    :per-page="perPage"
                    @change="onPageChange"
                    aria-controls="skills-table"
                  />
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_PAGE_SIZE } from '@/common/constants';
import Page from '@/components/Page.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';
import moment from 'moment';

export default {
  components: { MdIcon, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Skills Library',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalItems: 0,
      searchTerm: '',
      libraryItems: [],
      ordering: '',
      allPrograms: [],
      activeTab: 0, // 0 for My Skills, 1 for Transition Skills
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills Library', active: true },
      ];
    },

    mySkillsTableColumns() {
      return [
        { key: 'thumbnail', label: '', tdClass: 'clickable-item', thStyle: { width: '60px' } },
        { key: 'title', label: 'Title', tdClass: 'clickable-item', sortable: true },
        { key: 'description', label: 'Description', tdClass: 'clickable-item' },
        { key: 'program', label: 'Program', tdClass: 'clickable-item' },
        { key: 'created_at', label: 'Created', tdClass: 'clickable-item', sortable: true },
        { key: 'actions', label: '', tdClass: 'text-left', thStyle: { minWidth: '120px' } },
      ];
    },
    transitionSkillsTableColumns() {
      return [
        { key: 'thumbnail', label: '', tdClass: 'clickable-item', thStyle: { width: '60px' } },
        { key: 'title', label: 'Title', tdClass: 'clickable-item', sortable: true },
        { key: 'description', label: 'Description', tdClass: 'clickable-item' },
        { key: 'program', label: 'Program', tdClass: 'clickable-item' },
        { key: 'created_at', label: 'Created', tdClass: 'clickable-item', sortable: true },
        { key: 'actions', label: '', tdClass: 'text-left', thStyle: { minWidth: '120px' } },
      ];
    },
  },


  methods: {
    ...mapActions('skills', ['getSkillsLibrary', 'deleteSkillLibrary']),
    ...mapActions('program', ['getAllPrograms']),

    onTabChange(tabIndex) {
      this.activeTab = tabIndex;
      this.currentPage = 1;
      this.searchTerm = '';
      this.fetchLibraryItems();
    },

    async fetchLibraryItems(pageNum = 1, params = {}) {
      this.isLoading = true;
      if (document.getElementById('app')) {
        document.getElementById('app').scrollIntoView();
      }

      try {
        const isTransitionTab = this.activeTab === 1;
        const response = await this.getSkillsLibrary({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          is_superuser_skill: isTransitionTab,
          ...params,
        });
  
        // Handle the response structure: { status_code, data: { count, results: [...] }, message }
        if (response && response.data && Array.isArray(response.data.results)) {
          this.libraryItems = response.data.results;
          this.totalItems = response.data.count || this.libraryItems.length;
        } else if (response && Array.isArray(response.data)) {
          this.libraryItems = response.data;
          this.totalItems = this.libraryItems.length;
        } else if (Array.isArray(response)) {
          this.libraryItems = response;
          this.totalItems = this.libraryItems.length;
        } else {
          console.error('Unexpected response structure');
          this.libraryItems = [];
          this.totalItems = 0;
        }
        
        this.currentPage = pageNum;
      
      } catch (error) {
        this.libraryItems = [];
        this.totalItems = 0;
        this.makeToast({ variant: 'danger', msg: 'Failed to load library items' });
      }
      
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchLibraryItems(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchLibraryItems();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchLibraryItems();
    }, 500),

    editLibraryItem(data) {
      // Only allow editing for My Skills tab
      const isTransitionTab = this.activeTab === 1;
      if (!isTransitionTab) {
        this.$router.push({ name: 'edit-skill-library', params: { id: data.id } });
      }
    },

    viewLibraryItem(data) {
      // Navigate to view page for Transition Skills
      this.$router.push({ name: 'view-skill-library', params: { id: data.id } });
    },

    async removeLibraryItem(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove this item from the library. It will no longer be available.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          this.isLoading = true;
          await this.deleteSkillLibrary(id);
          this.fetchLibraryItems();
          this.makeToast({ variant: 'success', msg: 'Library item removed successfully!' });
          this.isLoading = false;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.isLoading = false;
      }
    },

    async fetchPrograms() {
      try {
        const response = await this.getAllPrograms({ ordering: 'id' });
        this.allPrograms = response.data || [];
        
      } catch (error) {
        this.allPrograms = [];
      }
    },

    getProgramName(programId) {
      const program = this.allPrograms.find(p => p.id === programId);
      return program ? program.title : '-';
    },

    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },

    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
    },
  },

  async mounted() {
    try {
      await this.fetchPrograms();
    } catch (error) {
      this.allPrograms = [];
    }
    await this.fetchLibraryItems();
  },
};
</script>
